import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShoppingBag, Plus, Minus, Loader2, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const CATS = ["All", "Apparel", "Accessories", "Collectibles"];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("All");
  const [cart, setCart] = useState({}); // lookup_key -> {product, qty}
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const add = (p) => { setCart((c) => ({ ...c, [p.lookup_key]: { product: p, qty: (c[p.lookup_key]?.qty || 0) + 1 } })); toast.success(`${p.name} added`); };
  const setQty = (lk, delta) => setCart((c) => {
    const qty = (c[lk]?.qty || 0) + delta;
    if (qty <= 0) { const n = { ...c }; delete n[lk]; return n; }
    return { ...c, [lk]: { ...c[lk], qty } };
  });

  const entries = Object.values(cart);
  const count = entries.reduce((s, e) => s + e.qty, 0);
  const total = entries.reduce((s, e) => s + (parseFloat(e.product.price_label.replace(/[^0-9.]/g, "")) * e.qty), 0);

  const checkout = async () => {
    setLoading(true);
    try {
      const items = entries.map((e) => ({ lookup_key: e.product.lookup_key, quantity: e.qty }));
      const { data } = await api.post("/store/checkout", { items, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed"); setLoading(false); }
  };

  const shown = products.filter((p) => cat === "All" || p.category === cat);

  return (
    <div className="section py-12 sm:py-16" data-testid="shop-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="kicker mb-2">Official Store</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-amber-50">Wear the Crown</h1>
          <p className="mt-3 max-w-xl text-amber-100/60">Premium merchandise celebrating African beauty, culture and heritage. Every purchase supports the movement.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="cart-open" className="relative btn-ghost-gold inline-flex items-center gap-2 shrink-0">
          <ShoppingBag className="h-4 w-4" /> Cart
          {count > 0 && <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full gold-bg text-obsidian text-[0.65rem] font-bold">{count}</span>}
        </button>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`shop-cat-${c.toLowerCase()}`}
            className={`rounded-full px-4 py-1.5 text-xs font-display uppercase tracking-wider transition-all ${cat === c ? "gold-bg text-obsidian" : "border border-gold/25 text-gold/70 hover:border-gold/60"}`}>{c}</button>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="product-grid">
        {shown.map((p) => (
          <div key={p.id} className="card-lux group flex flex-col" data-testid={`product-${p.lookup_key}`}>
            <div className="aspect-square overflow-hidden bg-obsidian-600">
              <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
            </div>
            <div className="flex flex-1 flex-col p-4">
              <p className="kicker mb-1 text-[0.6rem]">{p.category}</p>
              <h3 className="text-sm text-amber-50 leading-snug">{p.name}</h3>
              <p className="mt-1 font-mono text-lg gold-text">{p.price_label}</p>
              <button onClick={() => add(p)} data-testid={`add-${p.lookup_key}`} className="btn-gold mt-3 w-full text-xs py-2">Add to Cart</button>
            </div>
          </div>
        ))}
      </div>

      {/* Cart drawer */}
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={() => setOpen(false)}>
          <div className="h-full w-full max-w-md glass-strong p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="cart-drawer">
            <div className="flex items-center justify-between"><h2 className="font-serif text-2xl text-amber-50">Your Cart</h2><button onClick={() => setOpen(false)}><X className="h-5 w-5 text-gold" /></button></div>
            {entries.length === 0 ? <p className="mt-10 text-center text-muted-foreground">Your cart is empty.</p> : (
              <>
                <div className="mt-6 space-y-3">
                  {entries.map((e) => (
                    <div key={e.product.lookup_key} className="flex items-center gap-3 card-lux p-3">
                      <img src={e.product.image} alt="" className="h-14 w-14 rounded object-cover" />
                      <div className="min-w-0 flex-1"><p className="truncate text-sm text-amber-50">{e.product.name}</p><p className="font-mono text-xs gold-text">{e.product.price_label}</p></div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setQty(e.product.lookup_key, -1)} className="rounded-full border border-gold/30 p-1 text-gold"><Minus className="h-3 w-3" /></button>
                        <span className="w-5 text-center font-mono text-sm text-amber-50">{e.qty}</span>
                        <button onClick={() => setQty(e.product.lookup_key, 1)} className="rounded-full border border-gold/30 p-1 text-gold"><Plus className="h-3 w-3" /></button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex items-center justify-between border-t border-gold/15 pt-4">
                  <span className="text-amber-100/70">Total</span>
                  <span className="font-mono text-xl gold-text">${total.toFixed(2)}</span>
                </div>
                <button onClick={checkout} disabled={loading} data-testid="checkout-btn" className="btn-gold mt-5 w-full inline-flex items-center justify-center gap-2">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Checkout</button>
                <p className="mt-2 text-center text-xs text-muted-foreground">Secure Stripe checkout · Test card 4242 4242 4242 4242</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
